import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { FeatureCard, CTASection } from '../components/sections';
import { 
  Zap, 
  GraduationCap, 
  Compass,
  ArrowRight,
  Clock,
  Globe,
  Wrench
} from 'lucide-react';

const PILLARS = [
  {
    id: 'field-services',
    icon: Zap,
    title: 'Field Services',
    description: 'High-precision fiber splicing and tower site deployment. We support ISPs, contractors, and infrastructure owners with reliable field execution and safety-driven operations.',
    features: [
      'Fiber splicing (FTTH, backbone, enterprise)',
      'OTDR testing & certification',
      'Tower site installation (basic equipment, mounts, grounding)',
      'Small infrastructure deployments',
      'Field audits & quality verification'
    ],
    link: '/services',
    color: '#3b82f6'
  },
  {
    id: 'education',
    icon: GraduationCap,
    title: 'Education',
    description: 'Telecom fundamentals seminars for high-school and university students. We introduce young people to the world of telecommunications, spark curiosity, and encourage future careers in the industry.',
    features: [
      'Telecom fundamentals seminars',
      'Fiber optics demonstrations',
      'Infrastructure concepts explained',
      'Career pathway guidance',
      'University and school partnerships'
    ],
    link: '/education',
    color: '#10b981'
  },
  {
    id: 'advisory',
    icon: Compass,
    title: 'Advisory',
    description: 'Strategic guidance for network deployment and operations.',
    features: [
      'Deployment strategy',
      'Vendor and contractor evaluation',
      'Quality assurance frameworks',
      'Field operations optimization',
      'Technical documentation & SOP creation'
    ],
    link: '/advisory',
    color: '#8b5cf6'
  }
];

const EXPERIENCE_HIGHLIGHTS = [
  { 
    id: 'years', 
    icon: Clock, 
    title: '15+ Years of Experience', 
    description: 'Over a decade and a half working across fiber optics, network deployment, and field operations.' 
  },
  { 
    id: 'regions', 
    icon: Globe, 
    title: 'Experience Across 3 Regions', 
    description: 'Professional telecom work performed in Latin America, the United States, and Europe.' 
  },
  { 
    id: 'execution', 
    icon: Wrench, 
    title: 'Hands-On Field Execution', 
    description: 'Real-world experience in fiber splicing, tower site work, and infrastructure deployment.' 
  }
];

const ExperienceHighlights = () => (
  <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto animate-fade-in-up delay-300">
    {EXPERIENCE_HIGHLIGHTS.map((item) => (
      <div 
        key={item.id} 
        className="text-center bg-[#0f1d32]/50 rounded-2xl p-6 border border-[#1e3a5f]/30 card-hover"
      >
        <div className="w-14 h-14 rounded-xl bg-[#3b82f6]/20 flex items-center justify-center mx-auto mb-4">
          <item.icon className="w-7 h-7 text-[#3b82f6]" />
        </div>
        <h3 className="text-lg font-bold text-[#f1f5f9] mb-2">{item.title}</h3>
        <p className="text-sm text-[#94a3b8] leading-relaxed">{item.description}</p>
      </div>
    ))}
  </div>
);

const HomeHero = () => (
  <section className="relative overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#0f1d32] to-[#162544]" />
    <div 
      className="absolute inset-0 opacity-5"
      style={{
        backgroundImage: `linear-gradient(rgba(59, 130, 246, 0.3) 1px, transparent 1px),
                         linear-gradient(90deg, rgba(59, 130, 246, 0.3) 1px, transparent 1px)`,
        backgroundSize: '50px 50px'
      }}
    />
    <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#3b82f6]/10 rounded-full blur-3xl" />
    <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#8b5cf6]/10 rounded-full blur-3xl" />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
      <div className="text-center max-w-4xl mx-auto">
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 animate-fade-in-up">
          <span className="text-[#f1f5f9]">Building Networks.</span>
          <br />
          <span className="text-[#3b82f6]">Building Futures.</span>
        </h1>
        
        <p className="text-lg sm:text-xl text-[#94a3b8] mb-10 max-w-2xl mx-auto leading-relaxed animate-fade-in-up delay-100">
          Fiber splicing, tower deployment, and telecom education for the next generation.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-200">
          <Link to="/services">
            <Button className="btn-primary text-white font-medium px-8 py-6 text-lg rounded-xl">
              Services
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
          <Link to="/contact">
            <Button className="btn-secondary px-8 py-6 text-lg rounded-xl">
              Contact
            </Button>
          </Link>
        </div>
      </div>

      <ExperienceHighlights />
    </div>
  </section>
);

const PillarsSection = () => (
  <section className="py-24 bg-[#0f1d32]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-3xl sm:text-4xl font-bold text-[#f1f5f9] mb-4">
          What We Do
        </h2>
        <p className="text-[#94a3b8] max-w-2xl mx-auto">
          Comprehensive telecommunications services from field execution to education
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {PILLARS.map((pillar) => (
          <FeatureCard
            key={pillar.id}
            icon={pillar.icon}
            title={pillar.title}
            description={pillar.description}
            features={pillar.features}
            link={pillar.link}
            color={pillar.color}
          />
        ))}
      </div>
    </div>
  </section>
);

const Home = () => {
  return (
    <div className="min-h-screen bg-[#0a1628]">
      <HomeHero />
      <PillarsSection />
      <CTASection
        title="Ready to Build Together?"
        description="Whether you need field services, want to train your team, or require strategic guidance, we're here to help."
        actionLabel="Get in Touch"
        actionTo="/contact"
      />
    </div>
  );
};

export default Home;
